import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import aiDevelopmentService from "../../services/aiDevelopmentService";
import { safeRichHtml } from "./RichTextEditor";
import ImageLightbox from "./ImageLightbox";
import ImageAnalysisPanel from "./ImageAnalysisPanel";

/**
 * Renders sanitized rich text and turns every inline `img[data-attachment-id]`
 * placeholder into a real, authenticated image: resolves it via a blob fetch,
 * makes it open a zoom lightbox on click, and adds a discrete "view AI analysis"
 * affordance that stays hidden until the user asks for it.
 */
export default function RichContent({ html, attachments = [], className }) {
  const ref = useRef(null);
  const { t } = useTranslation();
  const [lightbox, setLightbox] = useState(null);
  const [analysisAttachmentId, setAnalysisAttachmentId] = useState(null);
  const [imageIds, setImageIds] = useState([]);
  const attachmentById = useMemo(
    () => Object.fromEntries(attachments.map((a) => [a.id, a])),
    [attachments],
  );

  useEffect(() => {
    const container = ref.current;
    if (!container) return undefined;
    const images = Array.from(container.querySelectorAll("img[data-attachment-id]"));
    const ids = images.map((img) => img.getAttribute("data-attachment-id"));
    setImageIds(ids);
    const urls = [];
    const listenerCleanups = [];
    let cancelled = false;
    images.forEach((img, i) => {
      const id = ids[i];
      const wrap = document.createElement("span");
      wrap.className = "ai-inline-image-wrap";
      img.replaceWith(wrap);
      wrap.appendChild(img);
      img.classList.add("ai-inline-image");
      img.tabIndex = 0;
      img.setAttribute("role", "button");
      img.setAttribute("aria-label", t("aiDevelopment.openImage"));
      const openLightbox = () => setLightbox({ index: i });
      const onKeyDown = (e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          openLightbox();
        }
      };
      img.addEventListener("click", openLightbox);
      img.addEventListener("keydown", onKeyDown);
      listenerCleanups.push(() => {
        img.removeEventListener("click", openLightbox);
        img.removeEventListener("keydown", onKeyDown);
      });
      aiDevelopmentService
        .attachmentBlob(id)
        .then((blob) => {
          if (cancelled) return;
          const url = URL.createObjectURL(blob);
          urls.push(url);
          img.src = url;
        })
        .catch(() => {
          if (!cancelled) wrap.classList.add("ai-inline-image-error");
        });
      const analysisButton = document.createElement("button");
      analysisButton.type = "button";
      analysisButton.className = "ai-image-analysis-toggle";
      analysisButton.setAttribute("aria-label", t("aiDevelopment.viewAiAnalysis"));
      analysisButton.textContent = "ⓘ";
      const onAnalysisClick = (e) => {
        e.stopPropagation();
        setAnalysisAttachmentId(id);
      };
      analysisButton.addEventListener("click", onAnalysisClick);
      listenerCleanups.push(() => analysisButton.removeEventListener("click", onAnalysisClick));
      wrap.appendChild(analysisButton);
    });
    return () => {
      cancelled = true;
      urls.forEach((url) => URL.revokeObjectURL(url));
      listenerCleanups.forEach((cleanup) => cleanup());
    };
    // attachments/attachmentById are intentionally excluded: this effect only wires up DOM nodes
    // parsed out of `html` (image src fetch, lightbox/analysis-panel triggers, all keyed by the
    // attachment id already embedded in the markup) and must run exactly once per html change -
    // adding attachments here would re-run it whenever the attachments list arrives/changes
    // (independently of html) and re-wrap/re-fetch/re-listen on already-wrapped nodes. The
    // analysis panel and lightbox below read attachmentById fresh at render time instead, so they
    // pick up attachments that arrive after html without needing this effect to re-run.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [html, t]);

  return (
    <div className={className}>
      <div ref={ref} dangerouslySetInnerHTML={safeRichHtml(html)} />
      {analysisAttachmentId && (
        <ImageAnalysisPanel
          attachment={attachmentById[analysisAttachmentId]}
          onClose={() => setAnalysisAttachmentId(null)}
        />
      )}
      {lightbox && (
        <ImageLightbox
          attachmentIds={imageIds}
          index={lightbox.index}
          attachmentById={attachmentById}
          onClose={() => setLightbox(null)}
          onNavigate={(index) => setLightbox({ index })}
        />
      )}
    </div>
  );
}
